/**
 * Mirror an uploaded object into the write-once `backups` bucket.
 *
 * Every object in the `documents` bucket is copied, at the same path, into
 * `backups`, which nothing — not the app, not the service-role key, not the
 * Supabase dashboard — can overwrite or delete. It is the second copy of every
 * binary in the product; see `docs/storage-backups.md` in
 * franklin-data-pipeline for the full design.
 *
 * The browser cannot do the copy itself: `backups` grants client roles no
 * access at all, deliberately, so that no org member can create objects nobody
 * is able to delete. The `mirror-storage-object` Edge Function authorises the
 * caller against the same building the storage RLS INSERT policy just checked,
 * then copies with the service role. The copy is server-side, so the bytes are
 * never re-uploaded — mirroring a 400 MB IFC costs one small request.
 *
 * CALL ORDER: only ever after `create_document_with_versioning` has confirmed a
 * `documents` row pointing at this path, never straight after the storage
 * upload. A backup cannot be deleted, so mirroring an object that no row ends
 * up owning — a failed RPC, a duplicate the RPC skipped, a soft-deleted row it
 * resurrected under its own old path, an upload the size check later rejects —
 * adds a permanent copy of a file nobody wants, and repeating the action adds
 * another every time. `rowOwnsUploadedObject` below is that check for callers
 * that read the RPC's answer directly. The bucket is `documents`; the Edge
 * Function resolves the source there and nowhere else.
 *
 * Never throws. The file is already safe in `documents` by the time this runs,
 * and failing an upload because its backup hiccuped would be strictly worse
 * than the missing backup. A miss is logged here and stays visible in the
 * `storage_backup_gaps` view until `scripts/backfill_storage_backups.py` (in
 * franklin-data-pipeline) closes it.
 */

import { supabase } from "@/integrations/supabase/client";

/**
 * Whether `create_document_with_versioning`'s `action` means a `documents` row
 * now points at the path that was just uploaded — the only case that may be
 * mirrored.
 *
 * `created`, `versioned`, `copied_with_extract` and `copied_with_artifacts`
 * insert a row carrying `p_file_path`. The other two answers do not:
 * `skipped` returns the pre-existing duplicate, and
 * `resurrected` restores a soft-deleted row **unchanged**, keeping its own old
 * `file_path` (it even returns that path back). Both leave the object just
 * uploaded owned by nothing, so a backup of it could never be deleted and every
 * retry would add one more.
 */
export function rowOwnsUploadedObject(action: unknown): boolean {
  return (
    action === "created" ||
    action === "versioned" ||
    action === "copied_with_extract" ||
    action === "copied_with_artifacts"
  );
}

export async function mirrorToBackups(filePath: string): Promise<boolean> {
  try {
    const { data, error } = await supabase.functions.invoke("mirror-storage-object", {
      body: { path: filePath },
    });

    if (error) {
      console.error("[backup-mirror] failed for", filePath, error);
      return false;
    }

    // `invoke` only fills `error` on a non-2xx response, so a 200 that reports
    // it did not mirror (or answers in a shape we don't recognise) would
    // otherwise be the one miss that leaves no trace on the client at all.
    if (data?.mirrored !== true) {
      console.error("[backup-mirror] not mirrored for", filePath, data);
      return false;
    }

    return true;
  } catch (e) {
    console.error("[backup-mirror] failed for", filePath, e);
    return false;
  }
}
